import message from 'antd/lib/message'
import _isEmpty from 'lodash-es/isEmpty'
import _isNumber from 'lodash-es/isNumber'
import _isArray from 'lodash-es/isArray'
import _isEqual from 'lodash-es/isEqual'
import _keys from 'lodash-es/keys'
import _mergeWith from 'lodash-es/mergeWith'
import { SetQuery } from 'next-query-params'
import {
  INITIAL_SEARCH_STATE,
  NUM_RESULTS_PER_PAGE,
  SEARCH_ATTRIBUTES_USING_OBJ,
} from './constants'
import { stringify } from 'qs'

export const formatLastSearchDates = (dateString, type) => {
  if (!dateString || dateString === 'Invalid Date') return null
  const newDate = new Date(dateString)
  return type === 'iso' ? newDate.toISOString() : newDate.toDateString()
}

export const getRequestBody = (page, searchState) => {
  const {
    searchText,
    title,
    source,
    country,
    company_name,
    company_group,
    company_industry,
    company_is_public,
    gt_posted_at,
    lt_posted_at,
    lt_revenue_per_apply,
    gt_revenue_per_apply,
    lt_revenue_per_click,
    gt_revenue_per_click,
    lt_revenue_per_hire,
    gt_revenue_per_hire,
    city,
    state,
    remote,
    location,
    is_accepts_anonymous_application,
    sort,
    sortDirection,
    userRid,
  } = searchState as typeof INITIAL_SEARCH_STATE

  return {
    ...(searchText && { full_text_search: searchText }),
    is_accepts_anonymous_application,
    ...(!_isEmpty(title) && { title: Object.keys(title) }),
    ...(!_isEmpty(source) &&
      Object.keys(source)?.filter((key) => !_isEmpty(key)).length && {
        source: Object.keys(source),
      }),
    ...(!_isEmpty(company_name) && {
      company_name: Object.keys(company_name),
    }),
    ...(!_isEmpty(company_group) && {
      company_group: Object.keys(company_group),
    }),
    ...(!_isEmpty(company_industry) && {
      company_industry: Object.keys(company_industry),
    }),
    ...(!_isEmpty(company_is_public) && {
      company_is_public: Object.keys(company_is_public),
    }),
    ...(!_isEmpty(country) && { country: Object.keys(country) }),
    ...(!_isEmpty(city) && { city: Object.keys(city) }),
    ...(!_isEmpty(state) && { state: Object.keys(state) }),
    ...(!_isEmpty(location) && { location: Object.keys(location) }),
    ...(!!gt_posted_at && {
      gt_posted_at: formatLastSearchDates(gt_posted_at, 'iso'),
    }),
    ...(!!lt_posted_at && {
      lt_posted_at: formatLastSearchDates(lt_posted_at, 'iso'),
    }),
    ...(_isNumber(lt_revenue_per_apply) && { lt_revenue_per_apply }),
    ...(_isNumber(gt_revenue_per_apply) && { gt_revenue_per_apply }),
    ...(_isNumber(lt_revenue_per_click) && { lt_revenue_per_click }),
    ...(_isNumber(gt_revenue_per_click) && { gt_revenue_per_click }),
    ...(_isNumber(lt_revenue_per_hire) && { lt_revenue_per_hire }),
    ...(_isNumber(gt_revenue_per_hire) && { gt_revenue_per_hire }),
    ...(!_isEmpty(remote) && { remote: Object.keys(remote) }),
    jobs_per_page: NUM_RESULTS_PER_PAGE,
    sort,
    ...(sort !== 'relevancy' && { direction: sortDirection }),
    page,
    //NOTE: omit user_rid for now - TC
    // user_rid: userRid,
    full_enrichment: false,
  }
}

export const createFiltersUuid = async (
  setQuery: SetQuery<any>,
  searchState: typeof INITIAL_SEARCH_STATE
) => {
  try {
    const response = await fetch('/api/search-queries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        search_query: {
          search_filters: searchState,
          search_query_type: 'jobs',
        },
      }),
    }).then((res) => {
      if (!res.ok) {
        throw new Error(res.statusText)
      }
      return res.json()
    })
    const uuid = response.search_query_uuid
    // setQuery({ uuid })
  } catch (e) {
    console.error(e)
  }
}

export const isReadyToSearch = (
  termParam: string,
  searchState: typeof INITIAL_SEARCH_STATE
) => {
  if (termParam) {
    const isSearchTermEqual = termParam === searchState.searchText
    if (!isSearchTermEqual) return false
  }
  return true
}

export const searchJobs = async (
  setSearchDetails: React.Dispatch<
    React.SetStateAction<{
      loading: boolean
      overview: any
      hasMore: boolean
      pageNum: number
    }>
  >,
  setSearchResults: React.Dispatch<React.SetStateAction<any[]>>,
  searchState: typeof INITIAL_SEARCH_STATE,
  adminAuthToken?: string
) => {
  setSearchDetails((searchDetails) => ({ ...searchDetails, loading: true }))
  try {
    const requestBody = getRequestBody(1, searchState)
    const reqQuery = stringify({ job: requestBody, adminAuthToken })

    const response = await fetch(`/api/search/jobs?${reqQuery}`).then((res) => {
      if (!res.ok) throw res.statusText
      return res.json()
    })

    setSearchResults(response.results)
    setSearchDetails((searchDetails) => ({
      ...searchDetails,
      loading: false,
      pageNum: 1,
      hasMore: !response.overview.last_page,
      overview: response.overview,
    }))
  } catch (e) {
    console.error('Could not search jobs: ', e)
    message.error('Could not search jobs.')
  }
}

export const fetchFilters = async (
  searchState: typeof INITIAL_SEARCH_STATE,
  customFilters: {
    [x: string]: any[]
  },
  setFiltersLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setFilters: React.Dispatch<
    React.SetStateAction<{
      [x: string]: any[]
    }>
  >,
  adminAuthToken?: string,
  userLocation?: { lat: string; long: string }
) => {
  const requestBody = getRequestBody(1, searchState)
  const reqQuery = stringify({
    job: {
      ...requestBody,
      show_aggregations: true,
      user_lat_long: [userLocation?.lat, userLocation?.long],
    },
    adminAuthToken,
  })

  try {
    setFiltersLoading(true)
    const response = await fetch(`/api/search/jobs?${reqQuery}`).then((res) => {
      if (!res.ok) throw res.statusText
      return res.json()
    })

    if (response.filters) {
      const withCustomFilters = _mergeWith(
        response.filters,
        customFilters,
        (objVal, src) => {
          if (_isArray(objVal)) {
            return objVal.concat(src)
          }
        }
      )
      setFilters(withCustomFilters)
      setFiltersLoading(false)
    }
  } catch (error) {
    console.error(error)
    setFiltersLoading(false)
  }
}

export const getSearchStateFromUuid = async (
  setSearchDetails: React.Dispatch<
    React.SetStateAction<{
      loading: boolean
      overview: any
      hasMore: boolean
      pageNum: number
    }>
  >,
  searchDetails: {
    loading: boolean
    overview: any
    hasMore: boolean
    pageNum: number
  },
  searchState: typeof INITIAL_SEARCH_STATE,
  query: any,
  setQuery: Function
): Promise<typeof INITIAL_SEARCH_STATE> => {
  try {
    setSearchDetails({ ...searchDetails, loading: true })
    const response = await fetch(`/api/search-queries?uuid=${query.uuid}`).then(
      (res) => {
        if (!res.ok) {
          throw new Error(res.statusText)
        }
        return res.json()
      }
    )
    //NOTE this search_filters is indeed searchState
    const searchStateInResponse = response.search_query[0].search_filters

    if (!_isEqual(searchStateInResponse, searchState)) {
      setQuery({ term: searchState.searchText || '' })
      setSearchDetails({ ...searchDetails, loading: false })
    }
    return searchStateInResponse as typeof INITIAL_SEARCH_STATE
  } catch (e) {
    console.warn('Failed to get job search filters from uuid', e)
    setQuery({ uuid: undefined })
  }
}

export const handleFilterParamInUrl = (
  filter: string,
  filterValue: string,
  resetState: () => void,
  updateStateByField: (
    property: keyof typeof INITIAL_SEARCH_STATE,
    value: string | { [x: string]: boolean }
  ) => void,
  callback: () => void
) => {
  resetState()
  const value = SEARCH_ATTRIBUTES_USING_OBJ.includes(filter)
    ? { [filterValue]: true }
    : filterValue

  updateStateByField(filter as keyof typeof INITIAL_SEARCH_STATE, value)
  callback?.()
}

export const handleCompanyNameParamInUrl = (
  companyNameParam: string,
  companyNameFilters: { [x: string]: any },
  updateStateByField: (
    property: keyof typeof INITIAL_SEARCH_STATE,
    value: string | { [x: string]: boolean }
  ) => void
) => {
  if (companyNameParam === _keys(companyNameFilters)[0]) return
  updateStateByField('company_name', { [companyNameParam]: true })
}

export const applyLastSearchTermOnUrl = (
  lastSearchText: string,
  currentSearchText: string,
  termParam: string,
  setQuery: Function
) => {
  const isLastSearchTextEmpty = _isEmpty(lastSearchText)
  const isSearchTextEmpty = _isEmpty(currentSearchText)
  if (
    !termParam &&
    !isLastSearchTextEmpty &&
    !isSearchTextEmpty &&
    currentSearchText === lastSearchText
  ) {
    setQuery({ term: currentSearchText || '' })
  }
}

export const generateInitialSearchState = (
  termParam: any,
  LAST_SEARCH_OBJ: any
): typeof INITIAL_SEARCH_STATE => ({
  ...INITIAL_SEARCH_STATE,
  searchText: termParam || LAST_SEARCH_OBJ?.searchText || '',
  source: LAST_SEARCH_OBJ?.source || {},
  company_name: LAST_SEARCH_OBJ?.company_name || {},
  company_group: LAST_SEARCH_OBJ?.company_group || {},
  company_industry: LAST_SEARCH_OBJ?.company_industry || {},
  company_is_public: LAST_SEARCH_OBJ?.company_is_public || {},
  is_accepts_anonymous_application:
    LAST_SEARCH_OBJ?.is_accepts_anonymous_application || false,
  state: LAST_SEARCH_OBJ?.state || {},
  city: LAST_SEARCH_OBJ?.city || {},
  remote: LAST_SEARCH_OBJ?.remote || {},
  country: LAST_SEARCH_OBJ?.country || {},
  title: LAST_SEARCH_OBJ?.title || {},
  location: LAST_SEARCH_OBJ?.location || {},
  selectedJob: LAST_SEARCH_OBJ?.selectedJob || null,
  gt_posted_at: formatLastSearchDates(LAST_SEARCH_OBJ?.gt_posted_at, 'date'),
  lt_posted_at: formatLastSearchDates(LAST_SEARCH_OBJ?.lt_posted_at, 'date'),
  sort: 'relevancy',
  sortDirection: 'desc',
  userRid: LAST_SEARCH_OBJ?.userRid || null,
})

export const statesAreEqual = (state1, state2) => {
  return JSON.stringify(state1) === JSON.stringify(state2)
}

export const fetchJobByJobRefAndUserLocation = async (
  jobRef: string,
  userLocation?: { lat: string; long: string }
) => {
  const reqQuery = stringify({
    job: {
      job_reference_clean: jobRef,
      sort: 'title',
      direction: 'desc',
      jobs_per_page: 1,
      page: 1,
      show_aggregations: false,
      user_lat_long: [userLocation?.lat, userLocation?.long],
      full_enrichment: true,
    },
  })

  const response = await fetch(`/api/search/jobs?${reqQuery}`).then((res) => {
    if (!res.ok) throw res.statusText
    return res.json()
  })

  if (!response.results.length) {
    console.error("Can't find job for this job ref")
  }
  return response.results[0]
}

export const scrollToList = (ref) => {
  ref.current.scrollIntoView()
}
